import { takeLatest, put, call } from 'redux-saga/effects';
import {
  GET_USERS_LIST,
  UPDATE_USER_STATUS,
  ADD_ADMIN_ANALYST,
  DOWNLOAD_USERS_CSV,
  VALIDATE_CORPORATE,
  getUsersListSuccess,
  getUsersListFailure,
  updateUserStatusSuccess,
  updateUserStatusFailure,
  addAdminAnalystSuccess,
  addAdminAnalystFailure,
  downloadUsersCSVSuccess,
  downloadUsersCSVFailure
} from './action';

import { AUTH_SERVER } from '../../../config';
import request from '../../../components/Utils/httpHelper';
import { toast } from 'react-toastify';
import { getErrorMessage } from './helper';
import { Preloading } from "../../../components/UI/Preloader/action";
import { CORE_API } from "../../../config";

/* ===================== COMMON ===================== */
const token = localStorage.getItem('id_token');

const httpHeaders = {
  'Content-Type': 'application/json',
  'Accept': 'application/json',
  Authorization: `Bearer ${token}`
};

const TAB_MAPPING = {
  admin: 'ROLE_RUBIXADMIN',
  analyst: 'ROLE_RUBIXANALYST',
  corporate: 'ROLE_CORPORATE'
};

/* ===================== LOAD USERS ===================== */
function* loadUsers({ payload }) {
  try {
    const { role, keyword = '', page = 0, size = 10 } = payload;
    const backendRole = TAB_MAPPING[role] || role;

    let url = `${AUTH_SERVER}/user/fetch-users?page=${page}&size=${size}`;
    if (backendRole) url += `&role=${backendRole}`;
    if (keyword) url += `&keyword=${encodeURIComponent(keyword)}`;

    const response = yield call(request, url, {
      method: 'GET',
      headers: httpHeaders
    });

    const apiData = response.data || {};
    const content = apiData.content || [];

    yield put(
      getUsersListSuccess(role, {
        content: content.map(u => ({ ...u, enabled: !!u.enabled })),
        totalPages: apiData.totalPages || 0,
        totalElements: apiData.totalElements || 0
      })
    );
  } catch (err) {
   const errorMessage = getErrorMessage(err, 'Failed to fetch users');
    yield put(getUsersListFailure(errorMessage));
    toast.error(errorMessage);
  }finally {
  yield put({ type: 'PRELOADING', payload: false });
}
}

/* ===================== UPDATE USER STATUS ===================== */
function* updateUserStatusSaga({ payload }) {
  try {
    const { id, status, role, page, size, keyword, corporateId } = payload;
    const newStatus = status === 'active' ? 'inactive' : 'active';
    let url = `${AUTH_SERVER}/user/enableordisable/${id}?status=${newStatus}`;
      if (corporateId) {
        url += `&corporateId=${corporateId}`;
      }
    yield call(request, url, { method: 'PUT', headers: httpHeaders });

    yield put(updateUserStatusSuccess());
    toast.success('User status updated successfully');

    yield call(loadUsers, { payload: { role, page, size, keyword } });
  } catch (err) {
   const errorMessage = getErrorMessage(err, 'Failed to update user status');
    yield put(updateUserStatusFailure(errorMessage));
    toast.error(errorMessage);
  }finally {
  yield put(Preloading(false));
}
}
/* ===================== ADD ADMIN / ANALYST ===================== */

function* addAdminAnalystSaga({ payload }) {
  try {

     yield put(Preloading(true));
    
     const {
      username,
      firstname,
      lastname,
      email,
      role,
      page = 0,
      pageSize = 10,
      keyword = '',
      activeTab = 'admin'
    } = payload;

    const roleEndpoints = {
      RUBIXADMIN: `${AUTH_SERVER}/user/admin/signup`,
      RUBIXANALYST: `${AUTH_SERVER}/user/analyst/signup`,
    };

    const requestURL = roleEndpoints[role];
    if (!requestURL) throw new Error(`No endpoint configured for role: ${role}`);

    yield call(request, requestURL, {
      method: 'POST',
      headers: httpHeaders,
      data: { username, firstname, lastname, email, role }
    });

    yield put(addAdminAnalystSuccess());

    toast.success(`User with role ${role} added successfully`);

    yield call(loadUsers, {
      payload: { role: activeTab, page, size: pageSize, keyword }
    });

  } catch (err) {
    const errorMessage = getErrorMessage(err, 'Failed to add user');
    yield put(addAdminAnalystFailure(errorMessage));
    toast.error(errorMessage);
  } finally {
    yield put(Preloading(false));
  }
}

/* ===================== DOWNLOAD USERS CSV ===================== */
function* downloadUsersCSVSaga({ payload }) {
  const { tab, preloading } = payload;

  if (tab === 'corporate') return;

  const backendRole = TAB_MAPPING[tab];
  if (!backendRole) {
    toast.error('Invalid role selected');
    return;
  }

  try {
    preloading(true);

    const url = `${AUTH_SERVER}/user/download/${backendRole}`;
    const headers = { Authorization: `Bearer ${token}`, Accept: 'text/csv' };

    const response = yield call(request, url, {
      method: 'GET',
      headers,
      responseType: 'blob'
    });

    const blob = new Blob([response.data], { type: 'text/csv' });
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', `${tab}-users.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(downloadUrl);

    yield put(downloadUsersCSVSuccess());
    toast.success('CSV downloaded successfully');
  } catch (err) {
    const errorMessage = getErrorMessage(err, 'Failed to download CSV');
    yield put(downloadUsersCSVFailure(errorMessage));
    toast.error(errorMessage);
  } finally {
    preloading(false);
  }
}




function* validateCorporateSaga({ payload, callback }) {
  try {
    const url = `${CORE_API}/corporate/getCorporateWithActiveInactiveProcessesById/${payload}`;

    const response = yield call(request, url, {
      method: "GET",
      headers: httpHeaders,
    });

    yield call(callback, null, response.data);

  } catch (err) {
    const errorMessage = getErrorMessage(err, "Invalid corporate ID");
    yield call(callback, errorMessage, null);
  }
}

/* ===================== WATCHERS ===================== */
export function* watchUsersSaga() {
  yield takeLatest(GET_USERS_LIST, loadUsers);
  yield takeLatest(UPDATE_USER_STATUS, updateUserStatusSaga);
  yield takeLatest(ADD_ADMIN_ANALYST, addAdminAnalystSaga);
  yield takeLatest(DOWNLOAD_USERS_CSV, downloadUsersCSVSaga);
  yield takeLatest(VALIDATE_CORPORATE, validateCorporateSaga);
}
